/**
 * Who is using the register (T07-22, design/07 §2, design/08 §1).
 *
 * 1. **Pairing** (「端末の登録」): the admin shows a one-time code for a register → the tablet sends
 *    it to identity-service `POST /v1/pos/registers/{id}/pair` → a 90-day **device token**
 *    (stored encrypted — `vault.ts`) + a 15-min device access token (memory).
 * 2. **PIN**: the staff member taps their name (or scans / types their badge) and the PIN →
 *    `POST /v1/pos/auth/pin` (device access token) → a 15-min **staff token** kept in memory only;
 *    renewed silently on activity (`/pos/auth/renew`) while the session lasts.
 * 3. **Lock**: 3 minutes without a touch (the register's `autoLockSeconds`) → PIN screen on top of
 *    the current screen (the basket stays); an expired staff token → PIN again before any money
 *    action; the admin revoked the tablet (401 `device_revoked`) → the token is deleted and the
 *    pairing screen explains why.
 *
 * `authReducer` is the pure state machine (unit-tested); `AuthController` does the I/O.
 */
import type { PosDatabase } from "./db";
import { ApiError } from "./errors";
import { clearDevice, loadDevice, saveDeviceToken, type StoredDevice } from "./vault";

export type LockReason = "start" | "idle" | "expired" | "signed_out";
export type UnpairedNotice = "revoked" | "expired" | null;

export interface Staff {
  id: string;
  name: string;
  role: string;
  locale: string | null;
  permissions: readonly string[];
}

export type AuthState =
  | { kind: "loading" }
  | { kind: "unpaired"; notice: UnpairedNotice }
  | { kind: "locked"; device: StoredDevice; reason: LockReason; lastStaff: Staff | null }
  | { kind: "unlocked"; device: StoredDevice; staff: Staff; expiresAt: number };

export type AuthEvent =
  | { type: "restored"; device: StoredDevice | null }
  | { type: "paired"; device: StoredDevice }
  | { type: "signed_in"; staff: Staff; expiresAt: number }
  | { type: "renewed"; expiresAt: number }
  | { type: "lock"; reason: LockReason }
  | { type: "unpaired"; notice: UnpairedNotice };

export function authReducer(state: AuthState, event: AuthEvent): AuthState {
  switch (event.type) {
    case "restored":
      return event.device
        ? { kind: "locked", device: event.device, reason: "start", lastStaff: null }
        : { kind: "unpaired", notice: null };
    case "paired":
      return { kind: "locked", device: event.device, reason: "start", lastStaff: null };
    case "signed_in":
      if (state.kind !== "locked" && state.kind !== "unlocked") {
        return state;
      }
      return {
        kind: "unlocked",
        device: state.device,
        staff: event.staff,
        expiresAt: event.expiresAt,
      };
    case "renewed":
      return state.kind === "unlocked" ? { ...state, expiresAt: event.expiresAt } : state;
    case "lock":
      if (state.kind === "unlocked") {
        return {
          kind: "locked",
          device: state.device,
          reason: event.reason,
          lastStaff: state.staff,
        };
      }
      if (state.kind === "locked") {
        return { ...state, reason: event.reason === "start" ? state.reason : event.reason };
      }
      return state;
    case "unpaired":
      return { kind: "unpaired", notice: event.notice };
  }
}

// ---- identity-service calls (an interface so tests can drive the controller) --------------------

export interface PairResult {
  deviceToken: string | null;
  deviceTokenExpiresAt: string | null;
  accessToken: string;
  expiresAt: string;
  device: { id: string; registerId: string; name: string };
}

export interface PinResult {
  accessToken: string;
  expiresAt: string;
  sessionExpiresAt: string;
  staff: { id: string; name: string; role: string; locale: string; permissions: string[] };
}

export interface ApprovalResult {
  approvalToken: string;
  expiresAt: string;
  approverId: string;
  approverName: string;
}

export interface ApprovalRequest {
  staffId?: string;
  badge?: string;
  pin: string;
  action: string;
  permission: string;
  subject: string;
  amount?: number | null;
}

export interface IdentityApi {
  /** `registerId` null = the typed code alone names its register (`POST /pos/auth/pair`). */
  pair(
    registerId: string | null,
    body: { pairingCode: string; deviceName: string; deviceInfo?: Record<string, unknown> },
  ): Promise<PairResult>;
  refreshDevice(deviceToken: string): Promise<PairResult>;
  pinLogin(
    deviceAccessToken: string,
    body: { staffId?: string; badge?: string; pin: string },
  ): Promise<PinResult>;
  renew(staffToken: string): Promise<PinResult>;
  approve(bearer: string, body: ApprovalRequest): Promise<ApprovalResult>;
}

// ---- controller -------------------------------------------------------------------------------

/** 401s about the manager's `X-Approval-Token`, not about the signed-in cashier. */
export const MANAGER_APPROVAL_CODES: ReadonlySet<string> = new Set([
  "approval_expired",
  "approval_invalid",
]);

/** Tokens this close to expiry are refreshed / renewed first. */
const REFRESH_MARGIN_MS = 30_000;
/** Renew the staff token on activity when it has less than this left. */
const RENEW_WHEN_LEFT_MS = 5 * 60_000;

interface MemoryToken {
  token: string;
  expiresAt: number;
}

export interface AuthControllerOptions {
  db: PosDatabase;
  identity: IdentityApi;
  now?: () => number;
}

function staffOf(result: PinResult): Staff {
  return {
    id: result.staff.id,
    name: result.staff.name,
    role: result.staff.role,
    locale: result.staff.locale || null,
    permissions: result.staff.permissions,
  };
}

export class AuthController {
  private state: AuthState = { kind: "loading" };
  private readonly listeners = new Set<() => void>();
  private readonly db: PosDatabase;
  private readonly identity: IdentityApi;
  private readonly now: () => number;
  private deviceToken: string | null = null;
  private deviceAccess: MemoryToken | null = null;
  private staffAccess: MemoryToken | null = null;
  private refreshing: Promise<string | null> | null = null;
  private renewing: Promise<void> | null = null;

  constructor(options: AuthControllerOptions) {
    this.db = options.db;
    this.identity = options.identity;
    this.now = options.now ?? Date.now;
  }

  // -- store (useSyncExternalStore) --
  getSnapshot = (): AuthState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private dispatch(event: AuthEvent): void {
    const next = authReducer(this.state, event);
    if (next !== this.state) {
      this.state = next;
      for (const listener of [...this.listeners]) {
        listener();
      }
    }
  }

  // -- lifecycle --
  async init(): Promise<void> {
    if (this.state.kind !== "loading") {
      return; // already restored (StrictMode runs effects twice; tests sign in first)
    }
    const stored = await loadDevice(this.db);
    this.deviceToken = stored?.deviceToken ?? null;
    if (stored?.device.expiresAt && Date.parse(stored.device.expiresAt) <= this.now()) {
      await this.unpair("expired");
      return;
    }
    this.dispatch({ type: "restored", device: stored?.device ?? null });
  }

  async pair(input: {
    registerId?: string | null;
    pairingCode: string;
    deviceName: string;
  }): Promise<void> {
    const result = await this.identity.pair(input.registerId ?? null, {
      pairingCode: input.pairingCode,
      deviceName: input.deviceName,
      deviceInfo: {
        app: "pos-pwa",
        userAgent: typeof navigator === "undefined" ? null : navigator.userAgent,
      },
    });
    if (!result.deviceToken) {
      throw new ApiError(500, "pairing_failed", "no device token");
    }
    const device: StoredDevice = {
      registerId: result.device.registerId,
      deviceId: result.device.id,
      deviceName: result.device.name,
      expiresAt: result.deviceTokenExpiresAt,
    };
    await saveDeviceToken(this.db, device, result.deviceToken);
    this.deviceToken = result.deviceToken;
    this.deviceAccess = { token: result.accessToken, expiresAt: Date.parse(result.expiresAt) };
    this.dispatch({ type: "paired", device });
  }

  async signIn(input: { staffId?: string; badge?: string; pin: string }): Promise<Staff> {
    const access = await this.deviceBearer();
    if (!access) {
      throw new ApiError(401, "device_revoked", "the tablet is not paired");
    }
    const result = await this.identity.pinLogin(access, input);
    const staff = staffOf(result);
    this.staffAccess = { token: result.accessToken, expiresAt: Date.parse(result.expiresAt) };
    await this.db.staffRecent.put({
      id: staff.id,
      name: staff.name,
      locale: staff.locale,
      lastSeenAt: this.now(),
    });
    this.dispatch({ type: "signed_in", staff, expiresAt: this.staffAccess.expiresAt });
    return staff;
  }

  /** Auto-lock / 「ロック」 button / expired token: the staff token is dropped, the screen stays. */
  lock(reason: LockReason): void {
    this.staffAccess = null;
    this.dispatch({ type: "lock", reason });
  }

  /** The admin revoked this tablet, or its token ran out: forget everything, pair again. */
  async unpair(notice: UnpairedNotice): Promise<void> {
    this.staffAccess = null;
    this.deviceAccess = null;
    this.deviceToken = null;
    await clearDevice(this.db);
    this.dispatch({ type: "unpaired", notice });
  }

  // -- tokens --
  /** A live staff token, or null (an expired one locks the screen: PIN again before money). */
  staffBearer(): string | null {
    const token = this.staffAccess;
    if (!token) {
      return null;
    }
    if (token.expiresAt <= this.now()) {
      this.lock("expired");
      return null;
    }
    return token.token;
  }

  /** The device access token (refreshed from the device token when needed). */
  async deviceBearer(): Promise<string | null> {
    const token = this.deviceAccess;
    if (token && token.expiresAt - REFRESH_MARGIN_MS > this.now()) {
      return token.token;
    }
    if (!this.deviceToken) {
      return null;
    }
    this.refreshing ??= this.refreshDevice().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  private async refreshDevice(): Promise<string | null> {
    const deviceToken = this.deviceToken;
    if (!deviceToken) {
      return null;
    }
    try {
      const result = await this.identity.refreshDevice(deviceToken);
      this.deviceAccess = { token: result.accessToken, expiresAt: Date.parse(result.expiresAt) };
      if (result.deviceToken && result.deviceToken !== deviceToken) {
        const state = this.state;
        const device: StoredDevice | null =
          state.kind === "locked" || state.kind === "unlocked"
            ? { ...state.device, expiresAt: result.deviceTokenExpiresAt ?? state.device.expiresAt }
            : null;
        if (device) {
          await saveDeviceToken(this.db, device, result.deviceToken);
        }
        this.deviceToken = result.deviceToken;
      }
      return result.accessToken;
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        await this.unpair(error.code === "device_token_expired" ? "expired" : "revoked");
        return null;
      }
      throw error;
    }
  }

  /** Bearer for a pos-service call: the staff token when signed in, else the device token. */
  async bearer(): Promise<string | null> {
    return this.staffBearer() ?? (await this.deviceBearer());
  }

  /** Activity: renew the staff token quietly when it has less than 5 minutes left. */
  touch(): void {
    const token = this.staffAccess;
    if (!token || this.renewing || token.expiresAt - this.now() > RENEW_WHEN_LEFT_MS) {
      return;
    }
    if (token.expiresAt <= this.now()) {
      this.lock("expired");
      return;
    }
    this.renewing = this.identity
      .renew(token.token)
      .then((result) => {
        this.staffAccess = { token: result.accessToken, expiresAt: Date.parse(result.expiresAt) };
        this.dispatch({ type: "renewed", expiresAt: this.staffAccess.expiresAt });
      })
      .catch(() => {
        // session over (8 h) or revoked: the next money action asks for the PIN
      })
      .finally(() => {
        this.renewing = null;
      });
  }

  /** A pos-service answer said the token is not good any more. */
  async handleUnauthorized(error: ApiError): Promise<void> {
    if (MANAGER_APPROVAL_CODES.has(error.code)) {
      // the manager's 2-minute approval ran out / is not valid: the cashier's own session is
      // fine — the approval dialog asks the manager again (T07-02 review)
      return;
    }
    if (error.code === "device_revoked" || error.code === "register_not_paired") {
      await this.unpair("revoked");
    } else if (this.state.kind === "unlocked") {
      this.lock("expired");
    }
  }

  /** Manager step-up (T07-18): PIN of the manager → one-use approval token for this action. */
  async approve(request: ApprovalRequest): Promise<ApprovalResult> {
    const bearer = await this.bearer();
    if (!bearer) {
      throw new ApiError(401, "unauthorized", "sign in first");
    }
    return this.identity.approve(bearer, request);
  }

  hasPermission(permission: string): boolean {
    return this.state.kind === "unlocked" && this.state.staff.permissions.includes(permission);
  }
}
